/** One registration per page. Only an explicit activation may reload a live page. */
export function createUpdateManager({ win, nav, doc, enabled = true }) {
  let state = { ready: false, moduleError: false, deferred: false, applying: false, error: false };
  let registration;
  let registrationPromise;
  let pending;
  let lastCheck = -Infinity;
  let stopListening;
  let cancelActivation;
  let observeRegistration;
  const listeners = new Set();
  const notify = (patch) => {
    state = { ...state, ...patch };
    listeners.forEach((listener) => listener());
  };
  const log = (result) => console.info('[web-update]', result);
  const rememberRegistration = () => {
    if (!registrationPromise) {
      registrationPromise = nav.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' })
        .then((value) => { registration = value; observeRegistration?.(value); return value; })
        .catch(() => { registrationPromise = undefined; log('registration-failed'); return null; });
    }
    return registrationPromise;
  };
  const check = () => {
    if (!enabled || !nav.serviceWorker || nav.onLine === false || doc.visibilityState === 'hidden') return Promise.resolve();
    if (pending) return pending;
    if (Date.now() - lastCheck < 10000) return Promise.resolve();
    lastCheck = Date.now();
    pending = (async () => {
      try {
        const reg = await rememberRegistration();
        if (!reg) return;
        if (!reg.installing) await reg.update();
        if (reg.waiting) notify({ ready: true });
        log(reg.waiting ? 'ready' : 'checked');
      } catch { log('check-failed'); }
      finally { pending = undefined; }
    })();
    return pending;
  };
  const start = () => {
    if (!enabled || !nav.serviceWorker) return () => {};
    let disposed = false;
    const cleanups = [];
    const watch = (target, event, listener) => {
      target.addEventListener(event, listener);
      cleanups.push(() => target.removeEventListener(event, listener));
    };
    const inspect = () => {
      if (!disposed && registration?.waiting) notify({ ready: true });
    };
    const workers = new Set();
    const registrations = new Set();
    const observeInstalling = () => {
      const worker = registration?.installing;
      if (worker && !workers.has(worker)) {
        workers.add(worker);
        watch(worker, 'statechange', () => Promise.resolve().then(inspect));
      }
      inspect();
    };
    observeRegistration = (reg) => {
      if (disposed || registrations.has(reg)) return;
      registrations.add(reg);
      watch(reg, 'updatefound', observeInstalling);
      observeInstalling();
    };
    const moduleFailed = () => {
      notify({ moduleError: true });
      check();
    };
    const moduleFailure = (event) => {
      const message = event.error?.message || event.reason?.message || event.message || '';
      if (/Importing a module script failed|dynamically imported module|Loading chunk/.test(message)) moduleFailed();
    };
    watch(win, 'error', moduleFailure);
    watch(win, 'unhandledrejection', moduleFailure);
    // React.lazy hands a failed chunk to the ErrorBoundary, so no window error fires: Vite's event is the only signal.
    // Not prevented: the page keeps its fallback and the notice decides, as for any other update.
    watch(win, 'vite:preloadError', moduleFailed);
    watch(win, 'online', check);
    watch(doc, 'visibilitychange', check);
    watch(win, 'heyjobNativeMessage', (event) => {
      if (event.detail?.type === 'APP_FOREGROUND') check();
    });
    const timer = win.setInterval(check, 5 * 60 * 1000);
    cleanups.push(() => win.clearInterval(timer));
    rememberRegistration().then((reg) => {
      if (disposed || !reg) return;
      observeRegistration?.(reg);
      check();
    });
    return () => {
      disposed = true;
      observeRegistration = undefined;
      cleanups.forEach((cleanup) => cleanup());
      cancelActivation?.();
    };
  };
  const apply = async () => {
    if (state.applying) return;
    notify({ applying: true, error: false });
    const reg = registration || await rememberRegistration();
    const worker = reg?.waiting;
    if (!worker) {
      log('reload-requested');
      win.location.reload();
      return;
    }
    let finished = false;
    const cleanup = () => {
      worker.removeEventListener('statechange', changed);
      win.clearTimeout(timer);
      cancelActivation = undefined;
    };
    const fail = () => {
      if (finished) return;
      finished = true;
      cleanup();
      notify({ applying: false, error: true });
      log('activation-failed');
    };
    const changed = () => {
      if (finished) return;
      if (worker.state === 'activated') {
        finished = true;
        cleanup();
        log('reload-applied');
        win.location.reload();
      } else if (worker.state === 'redundant') fail();
    };
    const timer = win.setTimeout(fail, 10000);
    cancelActivation = () => { finished = true; cleanup(); notify({ applying: false }); };
    worker.addEventListener('statechange', changed);
    try { worker.postMessage({ type: 'SKIP_WAITING' }); changed(); }
    catch { fail(); }
  };
  return {
    getSnapshot: () => state,
    subscribe(listener) {
      listeners.add(listener);
      if (listeners.size === 1) stopListening = start();
      return () => {
        listeners.delete(listener);
        if (!listeners.size) { stopListening?.(); stopListening = undefined; }
      };
    },
    check,
    apply,
    defer: () => notify({ deferred: true }),
  };
}
